import { getIdToken } from '../utils/firebase';
import { ConversationThread, ConversationDetail } from '../types/chat';
import api from './api';
import { parsePaywallError, type PaywallInfo } from '../utils/paywall';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

/**
 * Gemini turns on this endpoint run a tool loop (up to 5 round-trips), so the
 * shared axios 30s default is far too tight — but an unbounded fetch leaves the
 * widget spinning forever when the backend hangs. Matches the per-call override
 * convention in firApi / matterChatApi.
 */
const CHAT_TIMEOUT_MS = 90_000;

/** Mirrors ChatMessageRequest.mode in backend/app/schemas/chat.py. */
export type ChatMode = 'assistant' | 'research';

export const chatApi = {
  /**
   * Send a message and stream the response via SSE.
   * Calls onToken for each streamed text chunk, onDone when complete.
   */
  sendMessage: async (
    message: string,
    threadId: string | null,
    onToken: (text: string) => void,
    onDone: (data: { message_id: string; thread_id: string }) => void,
    /** `status` is the HTTP status when the server answered (e.g. 503 when
     *  research mode is switched off), undefined for network failures.
     *  `paywall` is set on a 402 (research mode needs a live plan + credits);
     *  the caller shows it in place — this client publishes nothing. */
    onError: (error: string, status?: number, paywall?: PaywallInfo) => void,
    onSources?: (sources: Array<{ label: string; detail: string; icon: string }>) => void,
    /** 'research' = the /research page (research prompt + judgment search);
     *  default 'assistant' = the floating widget, which sends deep research
     *  to /research. */
    mode: ChatMode = 'assistant',
  ): Promise<void> => {
    const token = await getIdToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept': 'text/event-stream',
    };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    // The active workspace, so the assistant answers for the dashboard the
    // lawyer is actually in. Every other request gets this from the axios
    // interceptor (services/api.ts); this call hand-rolls its headers because
    // it streams via fetch, so it has to set the header itself.
    try {
      const role = sessionStorage.getItem('nyayabodh_active_role');
      if (role) headers['X-Active-Role'] = role;
    } catch {
      /* storage unavailable — fall through to an all-areas answer */
    }

    const body = JSON.stringify({
      message,
      thread_id: threadId,
      mode,
    });

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), CHAT_TIMEOUT_MS);

    try {
      const response = await fetch(`${API_URL}/api/v1/chat`, {
        method: 'POST',
        headers,
        body,
        signal: controller.signal,
      });

      if (!response.ok) {
        let detail = `Chat request failed: ${response.status}`;
        let data: unknown = null;
        try {
          data = await response.json();
          const d = (data as { detail?: unknown } | null)?.detail;
          if (typeof d === 'string') detail = d;
        } catch {
          /* non-JSON error body — keep the status line */
        }
        const paywall = parsePaywallError({ response: { status: response.status, data } });
        onError(paywall?.message ?? detail, response.status, paywall ?? undefined);
        return;
      }

      const reader = response.body?.getReader();
      if (!reader) {
        onError('No response stream');
        return;
      }

      const decoder = new TextDecoder();
      let buffer = '';
      // MUST live outside the read loop. An SSE frame is two lines
      // ("event: sources" then "data: {...}") and the network can split a chunk
      // between them — declaring this inside the loop lost the event type
      // whenever that happened, silently misrouting the frame. Same bug class
      // already fixed in firApi and caseTheoryApi.
      let currentEventType = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('event: ')) {
            currentEventType = line.slice(7).trim();
            continue;
          }
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              if (currentEventType === 'sources' && data.sources && onSources) {
                onSources(data.sources);
              } else if (data.text) {
                onToken(data.text);
              }
              if (data.message_id && data.thread_id) {
                onDone(data);
              }
              if (data.error) {
                onError(data.error);
              }
              // Reset only after consuming a data line, so the next frame
              // starts clean but a split frame keeps its type.
              currentEventType = '';
            } catch {
              // Skip malformed JSON
            }
          }
        }
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        onError('The assistant took too long to respond. Please try again.');
      } else {
        onError(err instanceof Error ? err.message : 'Connection failed');
      }
    } finally {
      clearTimeout(timeoutId);
    }
  },

  listConversations: async (): Promise<ConversationThread[]> => {
    const response = await api.get('/chat/conversations');
    return response.data;
  },

  getConversation: async (threadId: string): Promise<ConversationDetail> => {
    const response = await api.get(`/chat/conversations/${threadId}`);
    return response.data;
  },

  deleteConversation: async (threadId: string): Promise<void> => {
    await api.delete(`/chat/conversations/${threadId}`);
  },
};
